import type { ErrorEvent, EventHint } from '@sentry/react';
import { ApiClientException } from './apiClientException';

/**
 * Erreurs de scripts tiers ou de l'hôte WordPress qui remontent par `window.onerror`
 * depuis la page /reservation/ : la frame fautive est le script inline de la page,
 * donc `denyUrls` ne les attrape pas. Relevé Sentry des 9 et 10 octobre 2026 :
 * - « Can't find variable: fbq » : Meta Pixel appelé par GTM avant d'être chargé (174 events) ;
 * - « r["@context"].toLowerCase » : script JSON-LD du thème (235 events) ;
 * - « Java object is gone » : pont natif des webviews Android (Instagram, Facebook) ;
 * - « unrecognized expression: [data-section=… » : jQuery du thème ;
 * - « before send for type `error` returned `null` » : journal interne du SDK Sentry.
 */
export const IGNORE_ERRORS: (string | RegExp)[] = [
  /Can't find variable: fbq/,
  /fbq is not defined/,
  /r\["@context"\]\.toLowerCase/,
  /Java object is gone/,
  /unrecognized expression: \[data-section=/,
  /before send for type `error` returned `null`/,
];

/**
 * Refus métier renvoyé par notre API WordPress (4xx) : « Only new clients qualify »,
 * « Required service not found », « Email already used »… L'utilisateur voit le message
 * dans un toast, le serveur a déjà laissé un breadcrumb ou un event de son côté, et
 * rien n'est corrigeable dans le front. Ces events pesaient 330 erreurs en deux jours.
 *
 * Exception : le retour 3DS (`flow: sca`). Un refus à cette étape arrive APRÈS
 * l'authentification bancaire ; il faut le voir.
 */
export function isBusinessApiError(event: ErrorEvent, hint: EventHint | undefined): boolean {
  const original = hint?.originalException;
  if (!(original instanceof ApiClientException)) return false;
  const code = Number(original.Code);
  if (!(code >= 400 && code < 500)) return false;
  if (event.tags?.flow === 'sca') return false;
  return true;
}
